import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useColorMode,
  useColorModeValue,
  Image,
  InputRightElement,
  IconButton,
  InputGroup,
  useToast,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { signupUser, loginUser } from "../../API/auth";
import LogoBlack from "../../images/logo.png";
import LogoWhite from "../../images/logo.png";

// Toast configuration
const TOAST_DEFAULTS = {
  position: "top-right",
  duration: 3000,
  isClosable: true,
};

const Register = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  // Theme-dependent values
  const logo = colorMode === "light" ? LogoBlack : LogoWhite;
  const textColor = useColorModeValue("black", "white");
  const bgColor = useColorModeValue("#FAFAF7", "#FAFAF7");

  // Form state
  const [formData, setFormData] = useState({
    userName: "",
    address: "",
    email: "",
    password: "",
    phoneNumber: "",
    role: "CLIENT",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Event handlers
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (btnLoading) return;

    try {
      setBtnLoading(true);

      // Register user
      await signupUser(formData);
      toast({
        ...TOAST_DEFAULTS,
        title: "Registration Successful",
        description: "You have successfully registered",
        status: "success",
      });

      // Auto-login after registration
      const loginResponse = await loginUser({
        email: formData.email,
        password: formData.password,
      });

      if (!loginResponse?.authToken) {
        throw new Error("Login failed after registration");
      }

      // Store user data in localStorage
      localStorage.setItem("token", loginResponse.authToken);
      localStorage.setItem("user", JSON.stringify(loginResponse.user));
      localStorage.setItem("role", loginResponse.user.role);
      localStorage.setItem("isUserLoggedIn", "true");
      localStorage.setItem("hasCompletedForm", "false");
      localStorage.setItem("firstTimeLogin", "true");

      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Registration error:", error);
      toast({
        ...TOAST_DEFAULTS,
        title: "Error",
        description: error.response?.data?.error || "Registration/Login Failed",
        status: "error",
      });
    } finally {
      setBtnLoading(false);
    }
  };

  const isFormValid = () =>
    formData.userName && formData.email && formData.password && formData.phoneNumber;

  return (
    <Flex
      direction="column"
      bg={bgColor}
      h="100vh"
      align="center"
      justify="center"
      px={4}
    >
      <Box w={{ base: "100%", md: "400px" }}>
        <form onSubmit={handleSubmit}>
          <Image mb={6} src={logo} alt="Logo" mx="auto" />
          <Heading color={textColor} fontSize="32px" mb="10px" textAlign="center">
            Register
          </Heading>
          <Text
            mb="12px"
            color={textColor}
            fontWeight="bold"
            fontSize="14px"
            textAlign="center"
          >
            Enter your details to register
          </Text>

          <FormControl mb="12px">
            <FormLabel fontSize="sm" color={textColor}>
              User Name
            </FormLabel>
            <Input
              name="userName"
              color={textColor}
              bg={bgColor}
              border="1px solid"
              borderColor={textColor}
              borderRadius="8px"
              fontSize="sm"
              size="lg"
              placeholder="Enter your username"
              _placeholder={{ color: "gray.500" }}
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
              value={formData.userName}
              onChange={handleInputChange}
              isRequired
            />
          </FormControl>

          <FormControl mb="12px">
            <FormLabel fontSize="sm" color={textColor}>
              Address
            </FormLabel>
            <Input
              name="address"
              color={textColor}
              bg={bgColor}
              border="1px solid"
              borderColor={textColor}
              borderRadius="8px"
              fontSize="sm"
              size="lg"
              placeholder="Enter your address"
              _placeholder={{ color: "gray.500" }}
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
              value={formData.address}
              onChange={handleInputChange}
            />
          </FormControl>

          <FormControl mb="12px">
            <FormLabel fontSize="sm" color={textColor}>
              Email
            </FormLabel>
            <Input
              name="email"
              color={textColor}
              bg={bgColor}
              border="1px solid"
              borderColor={textColor}
              borderRadius="8px"
              fontSize="sm"
              size="lg"
              placeholder="Enter your email address"
              _placeholder={{ color: "gray.500" }}
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
              value={formData.email}
              onChange={handleInputChange}
              isRequired
            />
          </FormControl>

          <FormControl mb="12px">
            <FormLabel fontSize="sm" color={textColor}>
              Password
            </FormLabel>
            <InputGroup>
              <Input
                name="password"
                color={textColor}
                bg={bgColor}
                border="1px solid"
                borderColor={textColor}
                borderRadius="8px"
                fontSize="sm"
                size="lg"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                _placeholder={{ color: "gray.500" }}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                value={formData.password}
                onChange={handleInputChange}
                isRequired
              />
              <InputRightElement width="4.5rem" mr="2">
                <IconButton
                  size="sm"
                  bg={bgColor}
                  onClick={togglePasswordVisibility}
                  icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <FormControl mb="12px">
            <FormLabel fontSize="sm" color={textColor}>
              Phone Number
            </FormLabel>
            <Input
              name="phoneNumber"
              color={textColor}
              bg={bgColor}
              border="1px solid"
              borderColor={textColor}
              borderRadius="8px"
              fontSize="sm"
              size="lg"
              placeholder="Enter your phone number"
              _placeholder={{ color: "gray.500" }}
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
              value={formData.phoneNumber}
              onChange={handleInputChange}
              isRequired
            />
          </FormControl>

          <Button
            variant="solid"
            colorScheme="green"
            fontSize="md"
            type="submit"
            w="100%"
            h="45px"
            mb="20px"
            mt="20px"
            isLoading={btnLoading}
            isDisabled={!isFormValid()}
          >
            REGISTER
          </Button>
          <Button
            as={Link}
            to="/"
            variant="solid"
            colorScheme="blue"
            fontSize="md"
            w="100%"
            h="45px"
            mb="20px"
          >
            SIGN IN
          </Button>
        </form>
      </Box>
    </Flex>
  );
};

export default Register;